import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { getWidthForElement, throttle } from '../../services/utilityFunctions';
import { useSwipeable } from '../../services/customHooks';

const Carousel = React.forwardRef(({ children, placeholder }, ref) => {
  const numItems = React.Children.count(children);

  const outerContainer = useRef(null);
  const innerContainer = useRef(null);

  const [{ itemSize, containerSize }, setSizes] = useState({
    itemSize: 0,
    containerSize: 0,
  });
  const [itemsScrolled, setItemsScrolled] = useState(0);
  const [{ showNextButton, showPrevButton }, setShowButtons] = useState({
    showPrevButton: false,
    showNextButton: false,
  });

  const handleWindowResize = useCallback(
    throttle(() => {
      let newItemSize = 0;
      let newContainerSize = 0;
      if (outerContainer.current) {
        newContainerSize = getWidthForElement(outerContainer.current);
      }
      if (innerContainer.current && innerContainer.current.firstChild) {
        newItemSize = getWidthForElement(innerContainer.current.firstChild);
      }
      setSizes({ itemSize: newItemSize, containerSize: newContainerSize });
    }, 100),
    [],
  );

  useEffect(
    () => {
      const itemsPerContainer = Math.floor(containerSize / itemSize);
      setShowButtons({
        showPrevButton: itemsScrolled > 0,
        showNextButton:
          numItems > 1 ? numItems - itemsScrolled > itemsPerContainer : false,
      });
    },
    [children, containerSize, itemSize, itemsScrolled],
  );

  useEffect(
    () => {
      handleWindowResize();
      window.addEventListener('resize', handleWindowResize);

      return () => window.removeEventListener('resize', handleWindowResize);
    },
    [children],
  );

  const handleWheel = useCallback(
    throttle(deltaX => {
      if (deltaX > 0 && showNextButton) {
        setItemsScrolled(is => is + 1);
      } else if (deltaX < 0 && showPrevButton) {
        setItemsScrolled(is => is - 1);
      }
    }, 200),
    [showNextButton, showPrevButton],
  );

  const showPlaceholder = React.Children.count(children) < 1 && placeholder;

  useImperativeHandle(ref, () => ({
    reset: () => setItemsScrolled(0),
    scrollToIndex: v => setItemsScrolled(v),
  }));

  const scrollMore = () => {
    setItemsScrolled(is => {
      if (is >= React.Children.count(children) - 1) {
        return is;
      }
      return is + 1;
    });
  };
  const scrollLess = () => {
    setItemsScrolled(is => {
      if (is <= 0) {
        return is;
      }
      return is - 1;
    });
  };

  const onSwipeRef = useSwipeable({
    onSwipedLeft: scrollMore,
    onSwipedRight: scrollLess,
  });

  const handleOuterRef = el => {
    if (el) {
      outerContainer.current = el;
      onSwipeRef(el);
    }
  };

  return (
    <div
      ref={handleOuterRef}
      className="carousel-container-wrapper"
      onWheel={({ deltaX }) => handleWheel(deltaX)}
    >
      {showPrevButton && (
        <button
          className="carousel-scroll prev"
          type="button"
          onClick={scrollLess}
        >
          <i className="fa fa-angle-left" />
        </button>
      )}
      <div
        className="carousel-container"
        ref={innerContainer}
        style={{
          transform: `translateX(-${itemSize * itemsScrolled}px)`,
        }}
      >
        {showPlaceholder ? placeholder : children}
      </div>
      {showNextButton && (
        <button
          className="carousel-scroll next"
          type="button"
          onClick={scrollMore}
        >
          <i className="fa fa-angle-right" />
        </button>
      )}
    </div>
  );
});

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  placeholder: PropTypes.element,
};

Carousel.defaultProps = {
  placeholder: null,
};

export default Carousel;
